import React from "react"
import {
    Button,
    ButtonVariant,
    ColorSchemes,
    componentStyles,
    LoadingOrErrorContainer,
    Spinner,
    Text,
    useColors,
    VerticalStack,
} from "@mallardbay/lib-react-components"

import AssistantConversationHistoryItem from "~components/outfitter-assistant/assistant-conversation-history-item"
import { useOutfitterAssistant } from "~contexts/outfitter-assistant-provider"
import useOutfitterAssistantConversationHistory from "~utils/hooks/use-outfitter-assistant-conversation-history"
import type { AssistantConversation } from "~utils/helpers/outfitter-assistant-conversation-history-helpers"
import COPY from "~config/copy-constants"
import { TEST_IDS } from "~config/constants"

export default function AssistantConversationHistory() {
    const styles = useStyles()
    const { chat, showChat } = useOutfitterAssistant()
    const {
        conversations,
        isLoading,
        error,
        hasMore,
        isLoadingMore,
        isLoadingMessages,
        loadMore,
        loadConversationMessages,
    } = useOutfitterAssistantConversationHistory()
    const {
        handleLoadMore,
        handleSelectConversation,
        handleStartNewConversation,
    } = useConversationHistoryActions({
        chat,
        loadConversationMessages,
        loadMore,
        showChat,
    })
    const isHistorySelectionDisabled = isLoadingMessages || chat.isLoading

    return (
        <LoadingOrErrorContainer
            isLoading={isLoading}
            error={error}
            shouldRenderChildren={!isLoading && !error}
            style={styles.root}
        >
            <Button
                variant={ButtonVariant.Outline}
                colorScheme={ColorSchemes.Brand}
                onClick={handleStartNewConversation}
                style={styles.newConversationButton}
            >
                {COPY.OUTFITTER_ASSISTANT_HISTORY_NEW_CONVERSATION}
            </Button>
            {!conversations.length && (
                <Text style={styles.empty}>
                    {COPY.OUTFITTER_ASSISTANT_HISTORY_EMPTY}
                </Text>
            )}
            {conversations.length > 0 && (
                <VerticalStack
                    style={styles.list}
                    testId={TEST_IDS.OUTFITTER_ASSISTANT_HISTORY_LIST}
                >
                    {conversations.map((conversation) => (
                        <AssistantConversationHistoryItem
                            key={conversation.id}
                            conversation={conversation}
                            isDisabled={isHistorySelectionDisabled}
                            onSelect={handleSelectConversation}
                        />
                    ))}
                </VerticalStack>
            )}
            {isLoadingMessages && (
                <Spinner size="sm" shouldUseTopMargin={false} />
            )}
            {hasMore && (
                <Button
                    variant={ButtonVariant.Outline}
                    onClick={handleLoadMore}
                    isLoading={isLoadingMore}
                >
                    {COPY.OUTFITTER_ASSISTANT_HISTORY_LOAD_MORE}
                </Button>
            )}
        </LoadingOrErrorContainer>
    )
}

function useConversationHistoryActions({
    chat,
    loadConversationMessages,
    loadMore,
    showChat,
}: {
    readonly chat: ReturnType<typeof useOutfitterAssistant>["chat"]
    readonly loadConversationMessages: ReturnType<
        typeof useOutfitterAssistantConversationHistory
    >["loadConversationMessages"]
    readonly loadMore: () => Promise<void>
    readonly showChat: () => void
}) {
    const latestSelectionIdRef = React.useRef(0)
    const handleStartNewConversation = React.useCallback(() => {
        latestSelectionIdRef.current += 1
        chat.startNewConversation()
        showChat()
    }, [chat, showChat])
    const handleSelectConversation = React.useCallback(
        async (conversation: AssistantConversation) => {
            const selectionId = latestSelectionIdRef.current + 1
            latestSelectionIdRef.current = selectionId
            try {
                const messages = await loadConversationMessages(conversation.id)
                if (selectionId !== latestSelectionIdRef.current) return
                chat.loadConversation({
                    conversationId: conversation.id,
                    messages,
                })
                showChat()
            } catch {
                // Query error state is rendered by LoadingOrErrorContainer.
            }
        },
        [chat, loadConversationMessages, showChat]
    )
    const handleLoadMore = React.useCallback(() => {
        void loadMore().catch(() => undefined)
    }, [loadMore])

    return {
        handleLoadMore,
        handleSelectConversation,
        handleStartNewConversation,
    }
}

function useStyles() {
    const colors = useColors()

    return componentStyles({
        root: {
            alignItems: "stretch",
            flex: 1,
            gap: 3,
            overflowY: "auto",
            padding: 4,
        },
        newConversationButton: {
            width: "100%",
            flexShrink: 0,
            marginBottom: 2,
        },
        empty: {
            color: colors.secondaryText,
            textAlign: "center",
            marginTop: 8,
        },
        list: {
            alignItems: "stretch",
            gap: 3,
            width: "100%",
        },
    })
}
